import { PropsWithChildren, useEffect } from 'react';
import Menu from '@engineering/icons/menu';
import Comment from '@engineering/icons/comment';
import Help from '@engineering/icons/help';
import User from '@engineering/icons/user';
import {
  BilligerMietwagenSiteViewContent,
  ContentLayout,
  HasInfoBanner,
  PageOfSiteProps,
} from '@silvertours/common-landingpages-view';
import {
  SupportPopover,
  useHomeFirstViewportVariant,
  useSiteInfo,
} from '@silvertours/front-entities';
import {
  PageLegacy,
  InfoBanner,
  Header,
  LoginModal,
  LoginForm,
  RegionalSettingsFlyout,
  useLogin,
  Footer,
} from '@silvertours/front-features';
import { useTranslations } from 'next-intl';
import { useStaticLinks } from './useStaticLinks';
import {
  LoginMobileMenuWrapper,
  RegionalSettingsWrapper,
  StyledHeader,
} from './Content.style';

type Page = HasInfoBanner;

const SupportMenu = () => <SupportPopover />;

const LoginMobileMenu = (_closeMenu: () => void, open: boolean) => {
  const { isLoggedIn, setShowMobileLogin } = useLogin();

  /*
   * The menu opens the form itself, so it reports the opening the way the
   * account button does. This also returns the form to the provider step,
   * which the user may have left on an earlier opening.
   */
  useEffect(() => {
    if (open) {
      setShowMobileLogin(true);
    }
  }, [open, setShowMobileLogin]);

  /*
   * The menu renders its content while it is closed. The provider step counts
   * an impression as it mounts, and one per page view would measure the wrong
   * thing, so nothing mounts before the user opens the menu.
   */
  if (!open) {
    return null;
  }

  if (isLoggedIn) {
    return <Header.LoggedInActions />;
  }

  return (
    <LoginMobileMenuWrapper>
      <LoginForm />
    </LoginMobileMenuWrapper>
  );
};

const ContentDisplay = ({
  children,
  page,
}: PageOfSiteProps<
  BilligerMietwagenSiteViewContent,
  ContentLayout<Page>,
  Record<string, unknown>
> &
  PropsWithChildren) => {
  const {
    getHeaderLinks,
    getFooterLinks,
    getLegalLinks,
    getSocialMediaLinks,
    getAppLinks,
  } = useStaticLinks();
  const headerLinks = getHeaderLinks(page.navigation.staticLinks);
  const footerLinks = getFooterLinks(page.navigation.staticLinks);
  const legalLinks = getLegalLinks(page.navigation.staticLinks);
  const socialMediaLinks = getSocialMediaLinks(page.navigation.staticLinks);
  const appLinks = getAppLinks(page.navigation.staticLinks);
  const t = useTranslations('features.navigationLegacy');

  const {
    featureToggles: { hideInfoline, regionalSettings },
  } = useSiteInfo();
  const isFloatingHeader = useHomeFirstViewportVariant() === 'variant';

  return (
    <PageLegacy.PageBase>
      <StyledHeader
        appearance={isFloatingHeader ? 'floating' : undefined}
        desktopActions={[
          {
            key: 'regional_settings',
            component: (
              <RegionalSettingsWrapper>
                <RegionalSettingsFlyout />
              </RegionalSettingsWrapper>
            ),
            hide: !regionalSettings || isFloatingHeader,
          },
          {
            key: 'infoline',
            component: (
              <Header.SupportPopoverButton
                popupContent={<SupportMenu />}
                icon={isFloatingHeader ? 'help' : 'contact'}
              />
            ),
            hide: hideInfoline,
          },
          {
            key: 'login',
            component: (
              <Header.LoginButton
                showIconOnDesktop={isFloatingHeader}
                appearance={isFloatingHeader ? 'floating' : 'default'}
              />
            ),
          },
        ]}
        desktopLinks={headerLinks}
        mobileMenus={[
          {
            id: 'mobile-menu-contact',
            closeLabel: t('navigation.header.closeLabel'),
            iconLabel: t('navigation.header.contact'),
            content: SupportMenu,
            icon: isFloatingHeader ? Help : Comment,
            hide: hideInfoline,
          },
          {
            id: 'mobile-menu-account',
            closeLabel: t('navigation.header.closeLabel'),
            iconLabel: t('login.menu.button.loggedIn'),
            content: LoginMobileMenu,
            icon: User,
          },
          {
            id: 'mobile-menu-hamburger',
            closeLabel: t('navigation.header.closeLabel'),
            iconLabel: t('navigation.header.menuLabel'),
            content: (closeMenu, open) => (
              <Header.MobileNavigationMenu
                links={headerLinks}
                navigationLabel={t('navigation.header.navigationLabel')}
                closeMenu={closeMenu}
                showRegionalSettings={regionalSettings}
                isOpen={open}
                appearance={isFloatingHeader ? 'floating' : 'default'}
              />
            ),
            icon: Menu,
            showInFloatingHeader: true,
          },
        ]}
        translations={{
          navigationLabel: t('navigation.header.navigationLabel'),
          hiddenLinksLabel: t('navigation.header.hiddenLinksLabel'),
        }}
      >
        {page.content?.infoBanner && (
          <InfoBanner {...page.content.infoBanner} />
        )}
      </StyledHeader>
      <LoginModal />
      {children}
      <Footer
        footerLinks={footerLinks}
        legalLinks={legalLinks}
        socialMediaLinks={socialMediaLinks}
        appLinks={appLinks}
      />
    </PageLegacy.PageBase>
  );
};

export { ContentDisplay };
